import { useState, useCallback } from 'react';
import type { SurveyInstance } from '@/types/survey.types';
import { MOCK_SURVEY_QUESTIONS } from '@/data/mockSurveys';
import { useOmitInstance } from '@/hooks/usePendingInstances';

export interface QuizAnswer {
  participantIndex: number;
  questionId: string;
  value: number | null; // null = skipped
}

export interface QuizState {
  instance: SurveyInstance;
  participantIndex: number;
  questionIndex: number;
  answers: QuizAnswer[];
  isComplete: boolean;
}

export interface UseSurveyQuizReturn {
  /** Remaining pending instances (first is the active one) */
  pending: SurveyInstance[];
  quiz: QuizState | null;
  answerQuestion: (value: number) => void;
  skipQuestion: () => void;
  /** Omit the entire current survey instance */
  omitSurvey: (callbacks?: { onSuccess?: () => void; onError?: (err: unknown) => void }) => void;
  /** Advance to next pending instance after quiz completion screen */
  continueAfterComplete: () => void;
  finalScore: number;
}

/** Sort: urgent (expiring < 24h) first, then by priority desc, then by meetingDate desc */
function sortInstances(instances: SurveyInstance[]): SurveyInstance[] {
  const now = Date.now();
  const urgentThreshold = 24 * 60 * 60 * 1000;
  return [...instances].sort((a, b) => {
    const aUrgent = (new Date(a.expiresAt).getTime() - now) < urgentThreshold;
    const bUrgent = (new Date(b.expiresAt).getTime() - now) < urgentThreshold;
    if (aUrgent !== bUrgent) return aUrgent ? -1 : 1;
    const aPri = a.priority ?? 0;
    const bPri = b.priority ?? 0;
    if (aPri !== bPri) return bPri - aPri;
    return new Date(b.meetingDate).getTime() - new Date(a.meetingDate).getTime();
  });
}

function initQuiz(instance: SurveyInstance): QuizState {
  return { instance, participantIndex: 0, questionIndex: 0, answers: [], isComplete: false };
}

function calcScore(answers: QuizAnswer[]): number {
  const rated = answers.filter(
    a => a.value !== null && MOCK_SURVEY_QUESTIONS.find(q => q.id === a.questionId)?.type === 'likert',
  );
  if (rated.length === 0) return 0;
  const sum = rated.reduce((acc, a) => acc + (a.value ?? 0), 0);
  return Math.round((sum / (rated.length * 4)) * 5 * 10) / 10;
}

export function useSurveyQuiz(initialInstances: SurveyInstance[]): UseSurveyQuizReturn {
  const [pending, setPending] = useState<SurveyInstance[]>(() => sortInstances(initialInstances));
  const [quiz, setQuiz] = useState<QuizState | null>(() => {
    const sorted = sortInstances(initialInstances);
    return sorted.length > 0 ? initQuiz(sorted[0]) : null;
  });
  const { mutate: omitInstance } = useOmitInstance();

  const advance = useCallback((value: number | null) => {
    setQuiz(prev => {
      if (!prev || prev.isComplete) return prev;

      const currentQ = MOCK_SURVEY_QUESTIONS[prev.questionIndex];
      const newAnswers: QuizAnswer[] = [
        ...prev.answers,
        { participantIndex: prev.participantIndex, questionId: currentQ.id, value },
      ];

      const nextQIdx = prev.questionIndex + 1;
      if (nextQIdx < MOCK_SURVEY_QUESTIONS.length) {
        return { ...prev, questionIndex: nextQIdx, answers: newAnswers };
      }

      const nextPIdx = prev.participantIndex + 1;
      if (nextPIdx < prev.instance.participants.length) {
        return { ...prev, participantIndex: nextPIdx, questionIndex: 0, answers: newAnswers };
      }

      return { ...prev, questionIndex: nextQIdx, answers: newAnswers, isComplete: true };
    });
  }, []);

  const answerQuestion = useCallback((value: number) => advance(value), [advance]);
  const skipQuestion = useCallback(() => advance(null), [advance]);

  const omitSurvey = useCallback((callbacks?: { onSuccess?: () => void; onError?: (err: unknown) => void }) => {
    if (quiz) omitInstance(quiz.instance.id, callbacks);
    setPending(prev => {
      const remaining = prev.slice(1);
      setQuiz(remaining.length > 0 ? initQuiz(remaining[0]) : null);
      return remaining;
    });
  }, [quiz, omitInstance]);

  const continueAfterComplete = useCallback(() => {
    setPending(prev => {
      const remaining = prev.slice(1);
      setQuiz(remaining.length > 0 ? initQuiz(remaining[0]) : null);
      return remaining;
    });
  }, []);

  const finalScore = quiz ? calcScore(quiz.answers) : 0;

  return { pending, quiz, answerQuestion, skipQuestion, omitSurvey, continueAfterComplete, finalScore };
}
